import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useMatchRoute } from "@tanstack/react-router";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BalanceCard } from "@/features/billing/balance-card";
import { balanceQuery, topUpMethodsQuery } from "@/features/billing/queries";
import { RequestTopUpDialog } from "@/features/billing/request-top-up/dialog";
import { StandingNotice } from "@/features/reseller/standing-notice";
import { m } from "@/lib/paraglide/messages.js";

// /billing/*: the balance header its pages share (guide → the billing tree), with the two tabs —
// the billing history and the top-ups — and the Top up button. The button is there only when the
// gateway lists a channel (docs/plans/billing-top-up.md T2) and the reseller may pay in: active,
// or suspended, since a suspension for an unpaid balance must be curable (T13). A pending reseller
// is refused by the API anyway. Every reseller reads the pages.
export const Route = createFileRoute("/_authenticated/billing")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(balanceQuery()),
      context.queryClient.ensureQueryData(topUpMethodsQuery()),
    ]),
  staticData: { crumb: m.billing_nav },
  component: BillingLayout,
});

const MAY_TOP_UP = new Set(["active", "suspended"]);

function BillingLayout() {
  const { reseller } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const matchRoute = useMatchRoute();
  const { data: balance } = useSuspenseQuery(balanceQuery());
  const { data: methods } = useSuspenseQuery(topUpMethodsQuery());
  const tab = matchRoute({ to: "/billing/top-ups" })
    ? "top-ups"
    : matchRoute({ to: "/billing", fuzzy: false })
      ? "history"
      : null;
  const canTopUp = methods.channels.length > 0 && MAY_TOP_UP.has(reseller.status);
  return (
    <>
      {reseller.status !== "active" && <StandingNotice status={reseller.status} />}
      <Item>
        <ItemContent>
          <ItemTitle>{m.billing_nav()}</ItemTitle>
          <ItemDescription>{m.billing_page_description()}</ItemDescription>
        </ItemContent>
        {canTopUp && (
          <ItemActions>
            <RequestTopUpDialog methods={methods} />
          </ItemActions>
        )}
      </Item>
      <BalanceCard balance={balance.balance} />
      {tab && (
        <Tabs
          value={tab}
          onValueChange={(value: unknown) =>
            void navigate({ to: value === "top-ups" ? "/billing/top-ups" : "/billing" })
          }
        >
          <TabsList>
            <TabsTrigger value="history">{m.billing_tab_history()}</TabsTrigger>
            <TabsTrigger value="top-ups">{m.billing_tab_top_ups()}</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <Outlet />
    </>
  );
}
